import { useTranslation } from "react-i18next"

import { CrewFeedLink } from "@/components/creator/crew-feed-link"
import { CrewRosterCell } from "@/components/creator/crew-roster-cell"
import { Card, CardContent } from "@/components/ui/card"
import type { CrewRoster } from "@/types/crew-hub"

/**
 * OKS-474 — §18 "Your Crew" roster grid (UX Spec §2.5).
 *
 * White card: header ("YOUR CREW" + the stubbed "See the feed →" link) over a
 * wrapping avatar grid of brand teammates (first-name + avatar + streak). The
 * grid wraps to more-per-row as width grows. When the roster is empty the
 * card is not rendered by the page (the empty-crew state covers it), so this
 * component assumes ≥1 member.
 */
interface CrewRosterGridProps {
  roster: CrewRoster
}

export function CrewRosterGrid({ roster }: CrewRosterGridProps) {
  const { t } = useTranslation()

  return (
    <section aria-labelledby="your-crew-heading">
      <Card>
        <CardContent className="flex flex-col gap-3 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <h2
              id="your-crew-heading"
              className="text-muted-foreground text-xs font-semibold tracking-wide uppercase"
            >
              {t("crew_hub.roster.title")}
            </h2>
            <CrewFeedLink />
          </div>

          <ul
            data-testid="crew-roster-grid"
            className="flex flex-wrap gap-x-3 gap-y-4"
          >
            {roster.members.map((member, i) => (
              <CrewRosterCell
                key={`${member.first_name}-${i}`}
                member={member}
              />
            ))}
          </ul>
        </CardContent>
      </Card>
    </section>
  )
}
